/**
 * ProjectMobileCardList — card-based render of the projects list for mobile breakpoints.
 *
 * The desktop table at >= md collapses below md to single-column on narrow viewports
 * (clipping Address, Stage, Confirmed, Invoiced, Paid, Updated). This component is
 * rendered alongside the table with `block md:hidden` (table wears `hidden md:block`)
 * so each viewport gets its appropriate layout.
 *
 * Pattern mirrors ClickupTaskListPanel's table-to-card transformation.
 */
import { cn } from '@/lib/utils';
import type { Project, WorkflowStage } from '@/lib/schemas';
import { ProjectStageBadge } from '@/components/projects/ProjectStageBadge';
import {
  evaluateProjectColors,
  rowTreatmentClasses,
  type ColorRule,
} from '@/lib/projectListColors';
import { ProjectColorDot, ProjectColorBadge } from '@/components/projects/ProjectColorIndicators';

interface ProjectMobileCardListProps {
  projects: Project[];
  view: string; // currentView from ProjectsPage — 'proposals' switches money column
  isArchived: boolean;
  /**
   * R2 project-list colouring rules (defaults to none). Same engine as the
   * desktop table, so desktop and mobile stay in parity. Empty ⇒ inert.
   */
  colorRules?: ColorRule[];
  onSelect: (project: Project) => void;
  formatRelativeTime: (dateString: string) => string;
}

function formatCurrency(value: number | undefined | null): string {
  if (value === undefined || value === null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

export function ProjectMobileCardList({
  projects,
  view,
  isArchived,
  colorRules = [],
  onSelect,
  formatRelativeTime,
}: ProjectMobileCardListProps) {
  const isProposals = view === 'proposals';

  return (
    <div
      className="block md:hidden space-y-2"
      data-testid="projects-mobile-cards"
    >
      {projects.map((project) => {
        // Money column — for proposals show Proposed (Required); for projects show Invoiced + Paid.
        const moneyPrimary = isProposals
          ? formatCurrency(project.proposed_required_total)
          : formatCurrency(project.invoiced_total);
        const moneyPrimaryLabel = isProposals ? 'Proposed' : 'Invoiced';
        const moneySecondary = isProposals
          ? null
          : { label: 'Paid', value: formatCurrency(project.paid_total) };

        // R2: resolve per-slot colour treatment — same engine as the desktop
        // table (parity). Empty when no rule matches ⇒ card renders as today.
        const treatment = evaluateProjectColors(project, colorRules);

        return (
          <button
            key={project.id}
            type="button"
            onClick={() => onSelect(project)}
            data-testid={`project-mobile-card-${project.id}`}
            className={cn(
              'w-full text-left rounded-lg border bg-white p-3 shadow-sm',
              'hover:bg-muted/50 active:bg-muted/70 transition-colors',
              'focus:outline-none focus:ring-2 focus:ring-ring',
              isArchived && 'opacity-60',
              // R2: border_left + row_tint appended last; empty string ⇒ inert.
              rowTreatmentClasses(treatment),
            )}
          >
            {/* Header row: name + project number */}
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="font-medium text-sm text-gray-900 truncate flex items-center gap-1.5">
                  <ProjectColorDot treatment={treatment} label={project.name} />
                  <span className="truncate">{project.name}</span>
                  <ProjectColorBadge treatment={treatment} label={project.category ?? undefined} />
                </p>
                {project.project_number && (
                  <p className="font-mono text-xs text-muted-foreground mt-0.5">
                    #{project.project_number}
                  </p>
                )}
              </div>
              {project.workflow_stage && (
                <div className="shrink-0">
                  <ProjectStageBadge
                    workflowStage={project.workflow_stage as WorkflowStage}
                    updatedAt={project.updated_at}
                  />
                </div>
              )}
            </div>

            {/* Address — secondary line, only if present */}
            {project.address && (
              <p className="text-xs text-muted-foreground mt-1.5 truncate">
                {project.address}
              </p>
            )}

            {/* Money + Updated row */}
            <div className="flex items-end justify-between gap-2 mt-2">
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  {moneyPrimaryLabel}
                </p>
                <p className="font-mono text-sm text-gray-900">{moneyPrimary}</p>
                {moneySecondary && (
                  <p className="font-mono text-[11px] text-muted-foreground">
                    {moneySecondary.label}: {moneySecondary.value}
                  </p>
                )}
              </div>
              {project.updated_at && (
                <p className="text-[11px] text-muted-foreground shrink-0">
                  {formatRelativeTime(project.updated_at)}
                </p>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}
